import { ProgressiveImage } from '../progressive-image';
import { getItemPhotoThumbUrl } from '../../lib/imageOptimization';
import { HeartIcon } from '@/assets/icons';

interface WishlistPhotoGridProps {
  wishPhotos: string[];
  emptyText: string;
  itemCount: number;
  /** Вариант бейджа - по умолчанию обычный, для публичных вишлистов - accent */
  badgeVariant?: 'default' | 'accent';
  /** Показать индикатор избранного (сердечко) */
  isFavorite?: boolean;
}

/**
 * Сетка фотографий вишлиста с адаптивной раскладкой
 * 
 * Раскладка зависит от количества фотографий (0-3)
 */
export function WishlistPhotoGrid({ 
  wishPhotos, 
  emptyText,
  itemCount,
  badgeVariant = 'default',
  isFavorite = false,
}: WishlistPhotoGridProps): JSX.Element {
  const optimizedPhotos = wishPhotos.map(photo => getItemPhotoThumbUrl(photo)).filter(Boolean) as string[];
  

  const badgeColorClasses = badgeVariant === 'accent'
    ? 'bg-accent text-white'
    : 'bg-accent text-accent-foreground';

  return (
    <div className="relative bg-muted aspect-[3/4]">
      {optimizedPhotos.length === 0 ? (
        // Пустое состояние - нет желаний
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-muted-foreground text-sm">{emptyText}</span>
        </div>
      ) : optimizedPhotos.length === 1 ? (
        // 1 фотография - на весь размер
        <ProgressiveImage
          src={optimizedPhotos[0]} 
          alt="" 
          className="w-full h-full object-cover"
          containerClassName="absolute inset-0"
        />
      ) : optimizedPhotos.length === 2 ? (
        // 2 фотографии - делим пополам вертикально
        <div className="absolute inset-0 grid grid-rows-2 gap-0.5">
          <ProgressiveImage
            src={optimizedPhotos[0]} 
            alt="" 
            className="w-full h-full object-cover"
            containerClassName="w-full h-full"
          />
          <ProgressiveImage
            src={optimizedPhotos[1]} 
            alt="" 
            className="w-full h-full object-cover"
            containerClassName="w-full h-full"
          />
        </div>
      ) : optimizedPhotos.length === 3 ? (
        // 3 фотографии - 1 большая сверху + 2 маленькие снизу
        <div className="absolute inset-0 grid grid-rows-2 gap-0.5">
          <ProgressiveImage
            src={optimizedPhotos[0]} 
            alt="" 
            className="w-full h-full object-cover"
            containerClassName="w-full h-full"
          />
          <div className="grid grid-cols-2 gap-0.5">
            <ProgressiveImage
              src={optimizedPhotos[1]} 
              alt="" 
              className="w-full h-full object-cover"
              containerClassName="w-full h-full"
            />
            <ProgressiveImage
              src={optimizedPhotos[2]} 
              alt="" 
              className="w-full h-full object-cover"
              containerClassName="w-full h-full"
            />
          </div>
        </div>
      ) : (
        // 4+ фотографии - сетка 2×2
        <div className="absolute inset-0 grid grid-rows-2 grid-cols-2 gap-0.5">
          <ProgressiveImage
            src={optimizedPhotos[0]} 
            alt="" 
            className="w-full h-full object-cover"
            containerClassName="w-full h-full"
          />
          <ProgressiveImage
            src={optimizedPhotos[1]} 
            alt="" 
            className="w-full h-full object-cover"
            containerClassName="w-full h-full"
          />
          <ProgressiveImage
            src={optimizedPhotos[2]} 
            alt="" 
            className="w-full h-full object-cover"
            containerClassName="w-full h-full"
          />
          <ProgressiveImage
            src={optimizedPhotos[3]} 
            alt="" 
            className="w-full h-full object-cover"
            containerClassName="w-full h-full"
          />
        </div>
      )}

      {/* Бейджи: счётчик желаний + индикатор избранного */}
      <div className="absolute top-2 right-2 flex items-center gap-1.5">
        {itemCount > 0 && (
          <div className={`${badgeColorClasses} rounded-full min-w-[28px] h-7 px-2 flex items-center justify-center text-xs font-semibold shadow-lg`}>
            {itemCount}
          </div>
        )}
        {isFavorite && (
          <div
            className="bg-white/90 backdrop-blur-sm rounded-full w-7 h-7 flex items-center justify-center shadow-lg"
            aria-label="В избранном"
          >
            <HeartIcon size={14} className="text-red-500 fill-red-500" />
          </div>
        )}
      </div>
    </div>
  );
}